import { useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardsContext } from '../contexts/DashboardsContext';
import { getDashboardQueriesFunc } from '../lib/helper';
import { DashboardQuery } from '../types/files';
import ObjectUtils from '../utils/ObjectUtils';

const useDashboardQueryFetch = <T>(dashboardId: string, queryTitle: string) => {
  const { mappedOverviewQueries, setDashboardsQueries } = useContext(DashboardsContext);
  const [queryLoaded, setQueryLoaded] = useState<boolean>(false);
  const [query, setQuery] = useState<T | undefined>(undefined);
  const dashboardIdRef = useRef<string>(dashboardId);

  const queryKey = useMemo(() => `${dashboardId}:${queryTitle}`, [dashboardId, queryTitle]);

  const { refetch } = useQuery({
    queryKey: ['dashboardQuery', `${dashboardId}:${queryTitle}`],
    queryFn: async () => !mappedOverviewQueries.has(queryKey)
      ? await getDashboardQueriesFunc(dashboardId, queryTitle) : [],
    staleTime: 5 * 60 * 1000,
    retry: 2,
    onSuccess: (bulkQueryData: DashboardQuery[]) => {
      if (!!bulkQueryData.length) {
        setDashboardsQueries(prev => produce(prev, draft => {
          bulkQueryData.forEach(newQuery => {
            if (!draft.find(query => query.dashboardId === newQuery.dashboardId && query.title === newQuery.title))
              draft.push(newQuery);
          });
        }));
        setQueryLoaded(true);
      } else {
        const timeOut = setTimeout(() => {
          clearTimeout(timeOut);
          setQueryLoaded(true);
        }, 30000);
      }
    },
  });

	useEffect(() => {
		if (!ObjectUtils.equalObjects(dashboardIdRef.current, dashboardId)) {
			dashboardIdRef.current = dashboardId;
			if (mappedOverviewQueries.has(queryKey)) {
				setQuery(mappedOverviewQueries.get(queryKey) || []);
				setQueryLoaded(true);
			} else {
        setQuery(undefined);
				setQueryLoaded(false);
        const timeOut = setTimeout(() => {
          clearTimeout(timeOut);
          setQueryLoaded(true);
          refetch();
        }, 15000);
			}
		} else if (mappedOverviewQueries.has(queryKey)) {
      setQuery(mappedOverviewQueries.get(queryKey) || '');
      setQueryLoaded(true);
    }
		// eslint-disable-next-line
	}, [mappedOverviewQueries, dashboardId]);

  return { queryLoaded, query };
};

export default useDashboardQueryFetch;
